import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Dialog,
  IconButton,
  InfoPlate,
  Loader,
  TextInput,
} from "fe-shared/src/components";
import { AddIcon, CheckIcon, CloseIcon } from "fe-shared/src/components/Icons";
import { MaterialCategoryName } from "fe-shared/src/types/domain";
import useValidation from "fe-shared/src/hooks/useValidation";
import useRecycleMaterials from "fe-shared/src/hooks/useRecycleMaterials";
import useProducts from "fe-shared/src/hooks/useProducts";
import formatTransactionNumber from "fe-shared-transactions/src/helpers/format";
import useWarehouseMaterials from "fe-shared-transactions/src/hooks/useWarehouseMaterials";
import { Transfer, Address } from "fe-shared-transactions/src/types";

import { ActionType, Company } from "../../types/inventoryAdjustments";

import InfoSection from "./components/InfoSection";
import MaterialRow from "./components/MaterialRow";
import {
  errorMessages,
  getInitialContent,
  getValidationConfig,
} from "./modules/validation";
import {
  AdjustmentMaterial,
  EditInventoryAdjustmentFormContent,
} from "./modules/types";
import { filterMaterialsByCategory } from "./modules/helpers";

import styles from "./EditInventoryAdjustmentDialog.module.scss";

interface Props {
  type: ActionType;
  onClose: () => void;
  onSubmit: (values: EditInventoryAdjustmentFormContent) => void;
  isDialogActive: boolean;
  action: Transfer;
  address?: Address;
  warehouseId: number;
  ownerCompany: Company;
  isLoading?: boolean;
}

const EditInventoryAdjustmentDialog = ({
  type,
  action,
  address,
  onClose,
  onSubmit,
  warehouseId,
  ownerCompany,
  isDialogActive,
  isLoading = false,
}: Props) => {
  const { t } = useTranslation();
  const { transferNumber, materials } = action;
  const { companyId, regNumber, displayName, businessRoleCode } = ownerCompany;
  const { materialCategoryName, units } = materials[0];
  const isPositive = type === "addition";
  const isNegative = type === "subtraction";
  const {
    data: recycleMaterials,
    isLoading: isRecycleMaterialsLoading,
  } = useRecycleMaterials([businessRoleCode]);
  const {
    data: producedProducts,
    isLoading: isProductsLoading,
  } = useProducts();
  const {
    data: warehouseMaterials,
    isFetching: isWarehouseMaterialsLoading,
  } = useWarehouseMaterials(companyId, warehouseId!);

  const mergedContent = useMemo(() => {
    return getInitialContent(materials);
  }, [materials]);

  const title = t(
    `inventoryAdjustment.details.categories.${materialCategoryName}`,
    {
      unit: units.unit,
    }
  );

  const {
    errors,
    values,
    isValid,
    validateAll,
    validateValue,
    updateValue,
    addArrayValue,
    removeArrayValue,
  } = useValidation(getValidationConfig(), mergedContent, errorMessages);
  const isSubmitDisabled = useMemo(
    () =>
      !!Object.keys(errors).length ||
      !Object.keys(values.materials).length ||
      isLoading,
    [errors, values.materials, isLoading]
  );

  const selectorOptions = useMemo(() => {
    if (isPositive) {
      const materialsForMapping =
        materialCategoryName === MaterialCategoryName.Tires
          ? recycleMaterials
          : producedProducts;

      return materialsForMapping
        ? materialsForMapping.map((material) => ({
            label: material.name,
            value: material.code,
          }))
        : [];
    }

    return filterMaterialsByCategory(
      warehouseMaterials,
      materialCategoryName
    ).map((material) => ({
      label: `${material.materialName} (${material.units.unitQuantity} ${material.units.unit})`,
      value: material.materialCode,
    }));
  }, [
    isPositive,
    materialCategoryName,
    producedProducts,
    recycleMaterials,
    warehouseMaterials,
  ]);

  const onSubmitButtonClick = () => {
    validateAll();
    if (isValid) {
      onSubmit(values);
    }
  };

  const isDataLoading =
    isProductsLoading ||
    isRecycleMaterialsLoading ||
    isWarehouseMaterialsLoading;

  const dialogActions: React.ReactNode[] = [
    <Button
      key="1"
      variant="filled"
      onClick={onSubmitButtonClick}
      iconComponent={CheckIcon}
      disabled={isSubmitDisabled}
      id="edit-inventory-adjustment-dialog-save-btn"
    >
      {t("submit")}
    </Button>,
    <Button
      key="2"
      variant="outlined"
      onClick={onClose}
      id="inventory-adjustment-action-dialog-cancel-button"
    >
      {t("cancel")}
    </Button>,
  ];

  const dialogTitle = (
    <div className={styles.editDialogHeader}>
      <div className={styles.editDialogHeaderTitle}>
        {t(`inventoryAdjustment.details.edit.actions.${type}`)}
      </div>
      <InfoPlate variant="black" className={styles.statusInfoPlate}>
        {formatTransactionNumber(transferNumber)}
      </InfoPlate>
    </div>
  );

  const onAddRow = () => {
    const emptyMaterial: AdjustmentMaterial = {
      key: Date.now().toString(),
      isOriginal: false,
      materialCode: "",
      materialName: "",
      units: {
        unit: units.unit,
        unitQuantity: 0,
      },
    };

    addArrayValue(`materials`, emptyMaterial);
  };

  const onRemoveRow = (i: number) => {
    removeArrayValue("materials", i);
  };

  return (
    <Dialog
      modal
      onClose={onClose}
      isActive={isDialogActive}
      width={800}
      actions={dialogActions}
      title={dialogTitle}
    >
      {isDataLoading && <Loader isBlocking />}
      {!isDataLoading && (
        <>
          <InfoSection
            address={address}
            companyRegNumber={regNumber}
            companyDisplayName={displayName}
          />
          <div className={styles.form}>
            <div className={styles.formSubheaderContainer}>
              <h2 className={styles.title}>{title}</h2>
              <span>{t("inventoryAdjustment.details.adjustments")}</span>
            </div>
            <div className={styles.dynamicFormButtonAdd}>
              <Button
                key="add-new-form-row"
                id="add-new-form-row"
                variant="inline"
                iconComponent={AddIcon}
                onClick={onAddRow}
                className={styles.dynamicFormButtonAdd}
              >
                {t(
                  `inventoryAdjustment.details.edit.form.add.${materialCategoryName}`
                )}
              </Button>
            </div>
            {values.materials.map((actionRow: AdjustmentMaterial, index) => (
              <div key={actionRow.key} className={styles.formMaterialRow}>
                <MaterialRow
                  adjustmentMaterial={actionRow}
                  materialOptions={selectorOptions}
                  valueKey={`materials[${index}]`}
                  onBlur={validateValue}
                  onChange={updateValue}
                  errors={errors}
                  isPositive={isPositive}
                  isNegative={isNegative}
                />
                <IconButton
                  id={`remove-row-${index}`}
                  aria-label="remove-row"
                  onClick={() => onRemoveRow(index)}
                  className={styles.formButtonRemove}
                >
                  <CloseIcon fillColor={null} />
                </IconButton>
              </div>
            ))}
            <TextInput
              id="edit-overall-adjustment-comment"
              title={t("inventoryAdjustment.details.edit.form.comments")}
              isMultiline
              required
              isError={!!errors.comment}
              errorText={errors.comment}
              value={values.comment}
              onBlur={() => validateValue("comment")}
              onChange={(event, value) => updateValue("comment", value)}
              labelClassName={styles.formTextLabel}
              wrapperClassName={styles.formTextWrapper}
            />
          </div>
        </>
      )}
    </Dialog>
  );
};

export default EditInventoryAdjustmentDialog;
